(function () {
  'use strict';
  const validID = value => /^[1-9]\d*$/.test(value || '') && Number.isSafeInteger(Number(value));
  const validPrice = value => /^\d+$/.test(value || '') && Number.isSafeInteger(Number(value));

  class BiancelleBoxes extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.tabs = [...this.querySelectorAll('[data-box-tab]')];
      this.panels = [...this.querySelectorAll('[data-box-panel]')];
      this.tabs.forEach(tab => tab.addEventListener('click', () => this.select(tab.getAttribute('aria-controls')), { signal: this.listeners.signal }));
      const productID = new URLSearchParams(window.location.search).get('box');
      const selected = this.panels.find(panel => panel.dataset.productId === productID);
      if (selected) this.select(selected.id);
      document.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('biancelle-box-builder');
        if (panel && this.contains(panel)) this.select(panel.id);
      }, { signal: this.listeners.signal });
    }
    select(id) {
      if (!this.panels.some(panel => panel.id === id) || this.panels.some(panel => panel.pending)) return;
      this.panels.forEach(panel => {
        if (panel.id !== id) panel.closePicker?.(false);
        panel.hidden = panel.id !== id;
      });
      this.tabs.forEach(tab => tab.setAttribute('aria-pressed', String(tab.getAttribute('aria-controls') === id)));
    }
    disconnectedCallback() { this.listeners?.abort(); }
  }

  class BiancelleBoxBuilder extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      this.capacity = Math.min(6, Math.max(1, Number(this.dataset.capacity) || 3));
      this.slots = [...this.querySelectorAll('[data-box-slot]')].slice(0, this.capacity);
      this.choices = this.choices || Array(this.capacity).fill(null);
      this.picker = this.querySelector('[data-box-picker]');
      this.submit = this.querySelector('[data-box-submit]');
      this.status = this.querySelector('[data-box-status]');
      this.cartLink = this.querySelector('[data-box-cart]');
      if (!this.picker || !this.submit || this.slots.length !== this.capacity) return;
      this.currency = new Intl.NumberFormat('ro-RO', { style: 'currency', currency: this.dataset.currency || 'RON' });
      this.options = [...this.querySelectorAll('[data-box-variant] option')];
      this.querySelectorAll('[data-box-aroma]').forEach(card => {
        const select = card.querySelector('[data-box-variant]');
        const refresh = () => {
          const choice = this.readOption(select?.selectedOptions[0]);
          const button = card.querySelector('[data-box-pick]');
          button.disabled = !choice;
          button.textContent = choice ? 'Alege aroma' : 'Indisponibil';
          card.querySelector('[data-box-aroma-price]').textContent = choice ? choice.money + ' separat' : 'Momentan indisponibilă';
        };
        select?.addEventListener('change', refresh, { signal });
        card.querySelector('[data-box-pick]').addEventListener('click', () => {
          const choice = this.readOption(select?.selectedOptions[0]);
          if (!choice || this.pending || this.activeSlot == null) return;
          this.choices[this.activeSlot] = choice;
          this.sync();
          this.closePicker();
        }, { signal });
        refresh();
      });
      this.querySelectorAll('[data-box-choose]').forEach(button => button.addEventListener('click', () => this.openPicker(Number(button.dataset.boxChoose)), { signal }));
      this.querySelectorAll('[data-box-remove]').forEach(button => button.addEventListener('click', () => {
        if (this.pending) return;
        const index = Number(button.dataset.boxRemove);
        this.choices[index] = null;
        this.sync();
        this.slots[index].querySelector('[data-box-choose]').focus();
      }, { signal }));
      this.querySelector('[data-box-close]').addEventListener('click', () => this.closePicker(), { signal });
      this.picker.addEventListener('cancel', event => { event.preventDefault(); this.closePicker(); }, { signal });
      this.picker.addEventListener('close', () => this.releasePicker(), { signal });
      this.picker.addEventListener('click', event => { if (event.target === this.picker) this.closePicker(); }, { signal });
      this.picker.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); this.closePicker(); return; }
        if (event.key !== 'Tab') return;
        const focusable = [...this.picker.querySelectorAll('button:not(:disabled), select:not([hidden]):not(:disabled), a[href]')];
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }, { signal });
      this.submit.addEventListener('click', () => this.addBox(), { signal });
      this.sync();
      this.classList.add('is-ready');
    }

    readOption(option) {
      if (!option || option.disabled || !validID(option.value) || !validPrice(option.dataset.price)) return null;
      return {
        id: option.value, price: Number(option.dataset.price),
        title: [option.dataset.productTitle, option.dataset.variantTitle].filter(Boolean).join(' · '),
        image: option.dataset.image || '', money: option.dataset.money || ''
      };
    }
    openPicker(index) {
      if (this.pending || !Number.isInteger(index) || index < 0 || index >= this.capacity) return;
      this.activeSlot = index;
      this.returnFocus = this.slots[index].querySelector('[data-box-choose]');
      this.querySelector('[data-box-picker-title]').textContent = 'Alege aroma ' + (index + 1) + ' din ' + this.capacity;
      if (typeof this.picker.showModal === 'function') this.picker.showModal();
      else {
        this.picker.setAttribute('open', '');
        this.picker.classList.add('bi-dialog-fallback');
        this.picker.setAttribute('aria-modal', 'true');
      }
      document.body.classList.add('bi-box-picker-open');
      this.querySelector('[data-box-close]').focus();
    }
    releasePicker() {
      if (!document.querySelector('[data-box-picker][open]')) document.body.classList.remove('bi-box-picker-open');
      if (this.restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus();
      this.restoreFocus = false;
    }
    closePicker(restoreFocus = true) {
      if (!this.picker?.hasAttribute('open')) return;
      this.restoreFocus = restoreFocus;
      if (typeof this.picker.close === 'function') this.picker.close();
      else this.picker.removeAttribute('open');
      this.releasePicker();
    }
    sync() {
      this.slots.forEach((slot, index) => {
        const choice = this.choices[index], image = slot.querySelector('[data-box-image]');
        const label = choice ? choice.title : 'Alege o aromă';
        slot.querySelector('[data-box-name]').textContent = label;
        slot.querySelector('[data-box-price]').textContent = choice ? 'Inclus în box' : 'Locul ' + (index + 1) + ' din ' + this.capacity;
        slot.querySelector('[data-box-plus]').hidden = !!choice?.image;
        image.hidden = !choice?.image;
        if (choice?.image) {
          if (image.getAttribute('src') !== choice.image) image.src = choice.image;
          image.alt = choice.title;
        } else { image.removeAttribute('src'); image.alt = ''; }
        const choose = slot.querySelector('[data-box-choose]');
        choose.disabled = !!this.pending;
        choose.setAttribute('aria-label', (choice ? 'Schimbă ' + choice.title : 'Alege aroma') + ' pentru locul ' + (index + 1));
        const remove = slot.querySelector('[data-box-remove]');
        remove.hidden = !choice;
        remove.disabled = !!this.pending;
        slot.classList.toggle('is-chosen', !!choice);
      });
      const selected = this.choices.filter(Boolean);
      this.querySelector('[data-box-count]').textContent = selected.length + ' din ' + this.capacity + ' locuri alese';
      const priced = validPrice(this.dataset.boxPrice);
      const total = Number(this.dataset.boxPrice);
      if (priced) this.querySelector('[data-box-total]').textContent = this.currency.format(total / 100);
      const separate = selected.reduce((sum, choice) => sum + choice.price, 0);
      const saving = selected.length === this.capacity && priced && separate > total;
      const reference = this.querySelector('[data-box-separate]'), badge = this.querySelector('[data-box-saving]');
      reference.hidden = !saving; badge.hidden = !saving;
      if (saving) {
        reference.textContent = this.currency.format(separate / 100) + ' separat';
        badge.textContent = 'Economisești ' + this.currency.format((separate - total) / 100);
      }
      this.submit.disabled = selected.length !== this.capacity || this.dataset.configured !== 'true' || !validID(this.dataset.boxId) || !priced || this.pending || this.locked;
      this.submit.setAttribute('aria-busy', String(!!this.pending));
      this.submit.textContent = this.pending ? 'Se adaugă…' : this.locked ? 'Verifică mai întâi coșul' : 'Adaugă boxul în coș +';
    }
    async addBox() {
      this.sync();
      if (this.submit.disabled) return;
      // Selections are order properties of a real, fixed-price Shopify box SKU.
      // This intentionally does not claim automatic component inventory deduction.
      const properties = { 'Box': this.dataset.title };
      const selected = this.choices.map(choice => this.options.find(option => option.value === choice.id && this.readOption(option)));
      if (selected.some(option => !option)) return;
      this.choices.forEach((choice, index) => {
        properties['Aroma ' + (index + 1)] = choice.title;
        properties['_aroma_' + (index + 1) + '_variant'] = choice.id;
      });
      const payload = { items: [{ id: Number(this.dataset.boxId), quantity: 1, properties }] };
      const drawer = document.querySelector('cart-drawer');
      let sections = [], accepted = false;
      this.pending = true;
      this.status.textContent = '';
      this.cartLink.hidden = true;
      this.sync();
      try {
        if (drawer?.getSectionsToRender && drawer?.renderContents) {
          sections = drawer.getSectionsToRender().slice(0, 5);
          payload.sections = sections.map(section => section.id);
          payload.sections_url = window.location.pathname;
          drawer.setActiveElement?.(this.submit);
        }
        const url = new URL(this.dataset.addUrl || '/cart/add', window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid cart origin');
        url.pathname = url.pathname.replace(/\.js$/, '') + '.js';
        const response = await fetch(url.pathname + url.search, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        const line = Array.isArray(data.items) && data.items.find(item => Number(item.variant_id || item.id) === Number(this.dataset.boxId));
        if (!response.ok || data.status || !line || !Object.entries(properties).every(([key, value]) => line.properties?.[key] === value)) throw new Error('Unconfirmed box');
        accepted = true;
        this.status.textContent = this.dataset.title + ' este în coș, cu aromele alese de tine.';
        this.cartLink.hidden = false;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'biancelle-boxes', cartData: data })).catch(() => {});
        }
        if (drawer && sections.length && sections.every(section => typeof data.sections?.[section.id] === 'string')) {
          for (const section of sections) {
            if (!new DOMParser().parseFromString(data.sections[section.id], 'text/html').querySelector(section.selector || '.shopify-section')) throw new Error('Missing cart markup');
          }
          drawer.renderContents({ ...data, id: line.id });
          drawer.classList.remove('is-empty');
        } else window.location.assign(this.dataset.cartUrl || '/cart');
      } catch (error) {
        if (accepted) window.location.assign(this.dataset.cartUrl || '/cart');
        else {
          this.locked = true;
          this.status.textContent = 'Nu am putut confirma adăugarea boxului. Verifică produsele din coș înainte de o nouă încercare.';
          this.cartLink.hidden = false;
        }
      } finally { this.pending = false; this.sync(); }
    }
    disconnectedCallback() { this.closePicker(false); this.listeners?.abort(); }
  }
  if (!customElements.get('biancelle-boxes')) customElements.define('biancelle-boxes', BiancelleBoxes);
  if (!customElements.get('biancelle-box-builder')) customElements.define('biancelle-box-builder', BiancelleBoxBuilder);
})();
